import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { aboutIntro, aboutChapters, aboutFacts, aboutAnchors, sustainabilityIntro, sustainabilityPillars, sustainabilityStats, saStoryIntro, saStoryPoints } from '../data/siteContent';
import { stories, rangePath } from '../data/mockData';
import { PageHero } from '../components/PageHero';
import { SectionNav } from '../components/SectionNav';
import { SectionHeading } from '../components/SectionHeading';
const EASE = [0.23, 1, 0.32, 1] as const;

/** One About page: Our Story, Sustainability and the South African story. */
export function About() {
  return <div className="w-full bg-paper" data-id="element-241" data-mp="2x8ettmx24mgu">
      <PageHero eyebrow="About" title="Our story." intro={aboutIntro} image="https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&q=80&w=2000" data-id="element-242" data-mp="2x7d8mpmbujle" />

      {/* In-page anchors */}
      <SectionNav items={aboutAnchors} data-id="element-243" data-mp="1uh9zrh4bx4rt" />

      {/* ── OUR STORY ─────────────────────────────────────────── */}
      <section id="our-story" className="scroll-mt-40" data-id="element-244" data-mp="1pjgwowyboqvr">
        <div className="mx-auto max-w-[1400px] px-6 py-16 md:py-20" data-id="element-245" data-mp="1pl1pah12tzqz">
          <div className="grid gap-y-10 border-y border-oat/70 py-12 sm:grid-cols-2 lg:grid-cols-4" data-id="element-246" data-mp="23k1f3s0bskoq">
            {aboutFacts.map(f => <div key={f.label} data-id="element-247" data-mp="2dgnwg6tg0tm0">
                <p className="type-display text-4xl text-coffee md:text-5xl" data-id="element-248" data-mp="2dfm9p532eiog">
                  {f.value}
                </p>
                <p className="type-eyebrow mt-3 text-[9px] text-coffee/45" data-id="element-249" data-mp="28h1i1u1vp7bm">
                  {f.label}
                </p>
              </div>)}
          </div>
        </div>

        {aboutChapters.map((c, i) => <div key={c.id} className={`w-full py-16 md:py-24 ${i % 2 === 1 ? 'bg-paper-warm' : ''}`} data-id="element-250" data-mp="2i9qkjzfgbaut" data-mpi={i}>
            <div className={`mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-2 lg:gap-20 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`} data-id="element-251" data-mp="2r9ly2awhbhy0">
              <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} className="overflow-hidden rounded-4xl" data-id="element-252" data-mp="1su5e50hicftp">
                <img src={c.image} alt="" className="aspect-[4/3] w-full object-cover" data-id="element-253" data-mp="2ck7bsmhxgzzm" />
              </motion.div>
              <div data-id="element-254" data-mp="2gjpzfq14vp65">
                <p className="type-eyebrow mb-6 text-[11px] text-pennant" data-id="element-255" data-mp="2lkncxpb77v1b">
                  {c.eyebrow}
                </p>
                <h2 className="type-display text-4xl text-coffee md:text-5xl" data-id="element-256" data-mp="m8kyithflqui">
                  {c.title}
                </h2>
                <p className="type-body mt-6 max-w-xl text-base text-ink/70" data-id="element-257" data-mp="r7e9ae5c33qz">
                  {c.body}
                </p>
              </div>
            </div>
          </div>)}
      </section>

      {/* ── SUSTAINABILITY ────────────────────────────────────── */}
      <section id="sustainability" className="scroll-mt-40" data-id="element-258" data-mp="9yli7ikuph8n">
        <div className="w-full bg-coffee py-20 text-paper md:py-24" data-id="element-259" data-mp="4yh0xqc4cihd">
          <div className="mx-auto max-w-[1400px] px-6" data-id="element-260" data-mp="50a6t3ewqyob">
            <p className="type-eyebrow mb-6 text-[11px] text-paper/55" data-id="element-261" data-mp="12mhbedb7or0o">
              Sustainability
            </p>
            <h2 className="type-display max-w-3xl text-4xl text-paper md:text-6xl" data-id="element-262" data-mp="xoftvwtfozrb">
              One cup, used properly.
            </h2>
            <p className="type-body mt-7 max-w-2xl text-lg text-paper/70" data-id="element-263" data-mp="spw6drz6zfwu">
              {sustainabilityIntro}
            </p>
          </div>
        </div>

        <div className="w-full bg-forest py-16 text-paper md:py-20" data-id="element-264" data-mp="3dfbps1w302cg">
          <div className="mx-auto grid max-w-[1400px] gap-10 px-6 sm:grid-cols-3" data-id="element-265" data-mp="39cxcryf3fwt3">
            {sustainabilityStats.map((s, i) => <motion.div key={s.label} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.06,
            ease: EASE
          }} data-id="element-266" data-mp="1137cjz0g84pr" data-mpi={i}>
                <p className="type-display text-6xl text-paper" data-id="element-267" data-mp="w5fj30k5oajg">{s.value}</p>
                <p className="type-body mt-3 max-w-[26ch] text-sm text-paper/70" data-id="element-268" data-mp="1jvi9zdcc3agh">
                  {s.label}
                </p>
              </motion.div>)}
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-269" data-mp="1asqv13k91wk3">
          <SectionHeading eyebrow="What it is made of" title="No mystery composite." subtitle="Three honest answers to the three questions people actually ask." data-id="element-270" data-mp="1ev3ytmffrke8" />
          <div className="mt-14 grid gap-x-12 gap-y-12 lg:grid-cols-3" data-id="element-271" data-mp="3gqgex3gbx5l">
            {sustainabilityPillars.map((p, i) => <motion.div key={p.id} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06,
            ease: EASE
          }} className="border-t border-oat pt-8" data-id="element-272" data-mp="3s12797d5cp8w" data-mpi={i}>
                <p className="type-eyebrow mb-4 text-[10px] text-pennant" data-id="element-273" data-mp="39blyjwrb5qhe">
                  {p.eyebrow}
                </p>
                <h3 className="type-display text-2xl text-coffee md:text-3xl" data-id="element-274" data-mp="34dan5hld77x2">
                  {p.title}
                </h3>
                <p className="type-body mt-4 text-sm text-ink/65" data-id="element-275" data-mp="3j2ypmm5a8cmd">{p.body}</p>
              </motion.div>)}
          </div>
        </div>

        <div className="w-full bg-paper-warm py-20 md:py-24" data-id="element-276" data-mp="2qkurj1usmc66">
          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 lg:grid-cols-2 lg:gap-20" data-id="element-277" data-mp="2qj9y5fk7sz9w">
            <div className="overflow-hidden rounded-4xl" data-id="element-278" data-mp="2ljxbovx99u7u">
              <img src="https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=1400" alt="Fynbos on a mountain slope" className="aspect-[4/3] w-full object-cover" data-id="element-279" data-mp="3b9432048i14e" />
            </div>
            <div data-id="element-280" data-mp="3p23g7vgjnj8s">
              <p className="type-eyebrow mb-6 text-[11px] text-pennant" data-id="element-281" data-mp="3p0ila4qbhyae">
                The honest part
              </p>
              <h3 className="type-display text-4xl text-coffee md:text-5xl" data-id="element-282" data-mp="2scvvgpnr1e5x">
                A cup you don’t use is worse than a paper one.
              </h3>
              <p className="type-body mt-6 max-w-lg text-base text-ink/70" data-id="element-283" data-mp="31bg5l2f8ufij">
                The maths only works if the cup gets carried. That is the whole
                reason we spend so long on the design, the weight and the lid —
                a beautiful cup gets used, and a used cup is the only kind that
                counts.
              </p>
              <Link to="/care" className="type-cta mt-8 inline-flex items-center gap-2 rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-284" data-mp="lmxzorsf5yy3">
                How to keep it going
                <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-285" data-mp="1wbrptillqdx6" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOUTH AFRICAN STORY ───────────────────────────────── */}
      <section id="sa-story" className="scroll-mt-40 bg-pennant py-20 text-paper md:py-28" data-id="element-286" data-mp="1ndpf2bc2g6wp">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-287" data-mp="1ifnypy60ylox">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20" data-id="element-288" data-mp="1dezx6ik66mgg">
            <div data-id="element-289" data-mp="18fnfsdc2i7l9">
              <p className="type-eyebrow mb-6 text-[11px] text-paper/60" data-id="element-290" data-mp="18g6snfmv25sf">
                The South African Ecoffee Cup<sup className="align-super" data-id="element-291" data-mp="37qy9itdrzygk">®</sup>{' '}
                story
              </p>
              <h2 className="type-display text-4xl text-paper md:text-6xl" data-id="element-292" data-mp="32ug6bxy3g86c">
                Made here, told properly.
              </h2>
              <p className="type-body mt-7 max-w-xl text-lg text-paper/75" data-id="element-293" data-mp="2bpvmadcqzomd">
                {saStoryIntro}
              </p>
              <div className="mt-9 flex flex-wrap gap-3" data-id="element-294" data-mp="22vqp7c70tgye">
                <Link to="/stories" className="type-cta inline-flex items-center gap-2 rounded-full bg-paper px-8 py-4 text-[11px] text-pennant transition-colors hover:bg-oat" data-id="element-295" data-mp="22v8n3yxog8ik">
                  Choose Your Story
                  <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-296" data-mp="1xxfg53ojvah8" />
                </Link>
                <Link to="/collections" className="type-cta inline-flex items-center gap-2 rounded-full border border-paper/30 px-8 py-4 text-[11px] text-paper transition-colors hover:bg-paper hover:text-pennant" data-id="element-297" data-mp="1jxo6tg8zai9k">
                  Official collections
                </Link>
              </div>
            </div>

            <div className="space-y-8" data-id="element-298" data-mp="16omdj8cfwc59">
              {saStoryPoints.map(p => <div key={p.title} className="border-t border-paper/20 pt-6" data-id="element-299" data-mp="16mjb1ou3oqjn">
                  <h3 className="type-ui text-base text-paper" data-id="element-300" data-mp="12p55c75mp0we">{p.title}</h3>
                  <p className="type-body mt-2 max-w-lg text-sm text-paper/65" data-id="element-301" data-mp="1gfgz0ahv37pn">
                    {p.body}
                  </p>
                </div>)}
              <div className="flex flex-wrap gap-2 pt-2" data-id="element-302" data-mp="1qcd9umv8g5xd">
                {stories.map(s => <Link key={s.id} to={rangePath(s)} className="type-cta rounded-full border border-paper/30 px-4 py-2.5 text-[10px] text-paper transition-colors hover:bg-paper hover:text-pennant" data-id="element-303" data-mp="1lebi7djqqyaa">
                    {s.name}
                  </Link>)}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}